import {useEffect, useMemo} from 'react';
import {
    Control,
    FieldErrors,
    SubmitHandler,
    useForm,
    UseFormRegister,
    UseFormSetValue,
    useWatch,
} from 'react-hook-form';
import {useDispatch} from 'react-redux';
import {toast} from 'react-toastify';
import {t} from '@lingui/core/macro';

import {serverAPI} from 'features/server/api/serverAPI';
import {serverAlertRuleAPI} from 'features/server/api/serverAlertRuleAPI';
import {
    ACCOUNT_SCOPE_VALUE,
    buildServerAlertRulePayload,
    getServerAlertRuleFormDefaults,
    SERVER_ALERT_RULE_MIN_FOR_MINUTES,
    SERVER_ALERT_RULE_MIN_RECOVER_MINUTES,
    validateServerAlertRuleForm,
} from 'features/server/lib/serverAlertRuleForm';
import {
    getServerAlertRuleOperatorLabel,
    normalizeServerAlertRuleOperator,
    SERVER_ALERT_RULE_OPERATORS,
} from 'features/server/lib/serverAlertRuleHelper';
import {fetchMe} from 'entities/user';
import {TypedDispatch} from 'shared/model/types';

import IServerAlertRule from './ServerAlertRule/IServerAlertRule';
import IServerAlertRuleFormState from './ServerAlertRule/IServerAlertRuleFormState';
import {IServerAlertRuleRecommendation} from './ServerAlertRule/IServerAlertRuleRecommendations';

export interface ISelectOption {
    value: string,
    label: string,
}

interface IUseServerAlertRuleFormParams {
    item?: IServerAlertRule,
    presetServerUuid?: string,
    onSuccess: () => void,
}

export interface IUseServerAlertRuleForm {
    isNew: boolean,
    control: Control<IServerAlertRuleFormState>,
    register: UseFormRegister<IServerAlertRuleFormState>,
    setValue: UseFormSetValue<IServerAlertRuleFormState>,
    errors: FieldErrors<IServerAlertRuleFormState>,
    isValid: boolean,
    isSaving: boolean,
    submit: ReturnType<ReturnType<typeof useForm<IServerAlertRuleFormState>>['handleSubmit']>,
    serverOptions: ISelectOption[],
    metricOptions: ISelectOption[],
    operatorOptions: ISelectOption[],
    severityOptions: ISelectOption[],
    requiresScope: boolean,
    metricRecommendation: IServerAlertRuleRecommendation | undefined,
    defaults: IServerAlertRuleFormState,
}

export const useServerAlertRuleForm = (
    {item, presetServerUuid, onSuccess}: IUseServerAlertRuleFormParams,
): IUseServerAlertRuleForm => {
    const isNew = !item;
    const dispatch = useDispatch<TypedDispatch>();

    const [addServerAlertRule, {isLoading: isLoadingCreate}] = serverAlertRuleAPI.useAddServerAlertRuleMutation();
    const [updateServerAlertRule, {isLoading: isLoadingUpdate}] =
        serverAlertRuleAPI.useUpdateServerAlertRuleMutation();
    const {data: recommendations} = serverAlertRuleAPI.useGetServerAlertRuleRecommendationsQuery();
    const {data: serversPagination} = serverAPI.useGetServersQuery({});

    const serverOptions = useMemo(() => [
        {value: ACCOUNT_SCOPE_VALUE, label: t`Все серверы (аккаунт)`},
        ...(serversPagination?.items ?? []).map(server => ({value: server.uuid, label: server.name})),
    ], [serversPagination]);

    const metricOptions = useMemo(() => (recommendations?.metrics ?? []).map(metric => ({
        value: metric.value,
        label: metric.label,
    })), [recommendations]);

    const operatorOptions = SERVER_ALERT_RULE_OPERATORS.map(operator => ({
        value: operator,
        label: getServerAlertRuleOperatorLabel(operator),
    }));

    const severityOptions = [
        {value: 'warning', label: t`Предупреждение`},
        {value: 'critical', label: t`Критично`},
    ];

    const defaults = useMemo(() => getServerAlertRuleFormDefaults(item, presetServerUuid), [item, presetServerUuid]);

    const {register, handleSubmit, formState, control, setError, clearErrors, setValue} =
        useForm<IServerAlertRuleFormState>({
            mode: 'onChange',
            defaultValues: defaults,
        });
    const {errors, isValid} = formState;

    const selectedMetric = useWatch({control, name: 'metric'});
    const requiresScope = !!recommendations?.metrics.find(metric => metric.value === selectedMetric)
        ?.requires_scope;
    const metricRecommendation = recommendations?.recommended.find(
        recommendation => recommendation.metric === selectedMetric,
    );

    useEffect(() => {
        if (metricRecommendation) {
            setValue(
                'operator',
                normalizeServerAlertRuleOperator(item?.operator ?? metricRecommendation.operator),
                {shouldDirty: false},
            );
            setValue('threshold', item?.threshold ?? metricRecommendation.threshold, {shouldDirty: false});
        }
    }, [metricRecommendation, item, setValue]);

    const watchedForMinutes = useWatch({control, name: 'for_minutes'});
    const watchedRecoverMinutes = useWatch({control, name: 'recover_minutes'});
    const watchedScope = useWatch({control, name: 'scope'});

    useEffect(() => {
        if (errors.for_minutes && watchedForMinutes >= SERVER_ALERT_RULE_MIN_FOR_MINUTES) {
            clearErrors('for_minutes');
        }
    }, [watchedForMinutes, errors.for_minutes, clearErrors]);

    useEffect(() => {
        if (errors.recover_minutes && watchedRecoverMinutes >= SERVER_ALERT_RULE_MIN_RECOVER_MINUTES) {
            clearErrors('recover_minutes');
        }
    }, [watchedRecoverMinutes, errors.recover_minutes, clearErrors]);

    useEffect(() => {
        if (!errors.scope) {
            return;
        }
        if (requiresScope && watchedScope) {
            clearErrors('scope');
        }
        if (!requiresScope && !watchedScope) {
            clearErrors('scope');
        }
    }, [watchedScope, requiresScope, errors.scope, clearErrors]);

    const onSubmit: SubmitHandler<IServerAlertRuleFormState> = async(formData) => {
        clearErrors();

        const validationErrors = validateServerAlertRuleForm(formData, requiresScope);
        if (validationErrors.length) {
            validationErrors.forEach(({field, message}) => setError(field, {message}));
            return;
        }

        const data = buildServerAlertRulePayload(formData, requiresScope);

        try {
            if (isNew) {
                await addServerAlertRule(data).unwrap();
                toast.info(t`Правило создано`);
                dispatch(fetchMe());
            } else {
                await updateServerAlertRule({uuid: item.uuid, ...data}).unwrap();
                toast.info(t`Правило изменено`);
            }
            onSuccess();
        } catch (error) {
        }
    };

    return {
        isNew,
        control,
        register,
        setValue,
        errors,
        isValid,
        isSaving: isLoadingCreate || isLoadingUpdate,
        submit: handleSubmit(onSubmit),
        serverOptions,
        metricOptions,
        operatorOptions,
        severityOptions,
        requiresScope,
        metricRecommendation,
        defaults,
    };
};

export default useServerAlertRuleForm;
